import { useState, useMemo, useRef, useEffect, Fragment } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    Target,
    Archive, LayoutDashboard,
    Folder, RotateCw,
    BookOpen, ListTodo, ChevronDown, MapPin, CalendarDays,
    Settings, Menu, Compass, Map,
    Sun, Workflow, Repeat, History, Store,
    Search, PanelLeftClose, ListTree,
    Gamepad2, Sparkles, Palette
} from 'lucide-react';
import IconRenderer from '../ui/IconRenderer';
import NavItem from '../ui/NavItem';
import Tooltip from '../ui/Tooltip';
import Logo from '../brand/Logo';
import useIdentityStore from '../../stores/useIdentityStore';
import useTimerStore from '../../stores/useTimerStore';
import useFlowStore from '../../stores/useFlowStore';
import useUserStore from '../../stores/useUserStore';
import useNavLayoutStore, { resolveNavSections, UNGROUPED_ID } from '../../stores/useNavLayoutStore';
import { resolveEntityColor } from '../../utils/entityColor';
import { FOCUS_PATHS } from '../Focus/focusModes';

import SyncStatus from '../ui/SyncStatus';
import RailToggle from './RailToggle';
import NavOrganizer from './NavOrganizer';
import './Sidebar.css';

const ICON_MAP = {
    LayoutDashboard, Target, Folder, ListTodo, Sun,
    BookOpen, Archive, RotateCw, MapPin, CalendarDays,
    Compass, Map, Workflow, Repeat, History, Store,
    Gamepad2, Sparkles, Palette
};

// ─── Unified Sidebar Navigation ──────────────────────────────────────────────
// ONE FLAT LIST. The old four-section taxonomy (orient / execute / organize /
// strategy) was a global assumption about how everybody thinks; it is gone.
// The order below is exactly the order those sections used to produce, so
// nothing moves for an existing user — only the headers disappear.
//
// There is likewise no "More" overflow drawer any more. It hid three real
// destinations (Store, Resources, Archives) behind a toggle that cost a click
// and a memory lookup to discover — the exact tax an ADHD-facing nav should not
// charge. They are ordinary rows at the end of this list now, still gated by
// `module` and, unlike before, organizable like everything else (the drawer was
// deliberately excluded from NavOrganizer; with the drawer gone there is no
// reason for three items to be second-class).
//
// Sections come back ONLY when the user turns on the `navGroups` module and
// defines their own groups (useNavLayoutStore + NavOrganizer). Items are still
// gated by `module`, exactly as before.
//
// Circuits and Rituals are NOT rows. They are modes of the Focus page, switched
// from the tab bar in the app header (components/Focus/FocusModeTabs.jsx). A
// sidebar row is earned by daily, unprompted use; ritual ticking already happens
// on Today, and a Circuit is something you enter from Focus. Their paths still
// exist and still deep-link — see components/Focus/focusModes.js.
const NAV_ITEMS = [
    // Home v2 (2026-08-30): the AI-first landing screen took `/`. Today kept every
    // one of its regions and moved to `/today` — it is second, not gone.
    { path: '/', label: 'Home', icon: 'Sparkles' },
    { path: '/today', label: 'Today', icon: 'Sun' },
    { path: '/dashboard', label: 'Dashboard', icon: 'LayoutDashboard', module: 'dashboard' },
    { path: '/focus', label: 'Focus', icon: 'Target' },
    { path: '/sessions', label: 'Logs', icon: 'History' },
    { path: '/tasks', label: 'Tasks', icon: 'ListTodo' },
    { path: '/projects', label: 'Projects', icon: 'Folder', module: 'projects' },
    { path: '/areas', label: 'Areas', icon: 'MapPin', module: 'areas' },
    { path: '/calendar', label: 'Calendar', icon: 'CalendarDays', module: 'calendar' },
    { path: '/quests', label: 'Quests', icon: 'Map', module: 'quests' },
    { path: '/vision', label: 'Vision', icon: 'Compass', module: 'vision' },
    {
        label: 'Reviews', icon: 'RotateCw', type: 'group', module: 'reviews',
        path: '/reviews',
        children: [
            { path: '/reviews', label: 'Hub' },
            { path: '/reviews?tab=weekly', label: 'Weekly' },
            { path: '/reviews?tab=quarterly', label: 'Quarterly' },
        ]
    },
    // Formerly behind "More". Store is ambient-core (never gated); the other two
    // keep the module gates they always had.
    { path: '/store', label: 'Store', icon: 'Store' },
    { path: '/resources', label: 'Resources', icon: 'BookOpen', module: 'resources' },
    { path: '/archives', label: 'Archives', icon: 'Archive', module: 'archives' },

    // Ambient core, never gated — same treatment as Store above: a "go do a
    // specific thing" utility a user summons rather than a page they browse, so
    // the row sits after the content/organize cluster instead of inside it. It
    // was previously unreachable: Gamification's only in-app link was a button
    // on Dashboard, itself a module and off by default.
    { path: '/gamification', label: 'Gamification', icon: 'Gamepad2' },

    // There is deliberately NO 'AI Coach' row any more. Home v2 IS the AI
    // surface — `/ai-coach` redirects to `/` (App.jsx) and Home renders that
    // page's thread view inline — so a second row would be a different door to
    // the same screen. The header's Coach icon still opens the floating widget
    // from anywhere; that is unchanged.

    { path: '/design-system', label: 'Design System', icon: 'Palette', devOnly: true },
];

const PLAN_LABELS = {
    founder: 'Founder',
    premium: 'Premium',
    pro: 'Pro',
};

/**
 * Sidebar — desktop navigation, ported from Figma's `Nav / Sidebar`.
 *
 * COLLAPSE STATE IS NOT OWNED HERE. AppShell is the single owner: it holds the
 * persisted preference, forces the rail during a focus session and on tablet
 * widths, and hands the result down as `collapsed`. This component only renders
 * it and asks for a toggle. (Previously two components owned the same flag and
 * three writers raced, two of them skipping persistence.)
 *
 * ─── Header ──────────────────────────────────────────────────────────────────
 * EXPANDED the brand is just the brand: `<Logo variant="lockup">`, no button,
 * no morph. Collapsing is an EXPLICIT control in the right-hand cluster
 * (search / customise / close), so nothing about hovering the logo is
 * load-bearing any more.
 * COLLAPSED the rail has no room for that cluster, so the mark itself carries
 * the affordance again — that is all RailToggle is now.
 * Rendering two different components (rather than teaching RailToggle to stop
 * morphing) is what makes the rule structural: while expanded there is no
 * button in the brand slot at all, so it cannot morph, cannot be tabbed to and
 * cannot claim an `aria-expanded` it no longer owns.
 *
 * @param {boolean}  collapsed          render as the 56px rail
 * @param {Function} onToggleCollapse   ask AppShell to flip the rail
 */
export default function Sidebar({
    mobileOpen = false,
    setMobileOpen = () => {},
    collapsed = false,
    onToggleCollapse = () => {},
}) {
    const navigate = useNavigate();
    const location = useLocation();
    const [identityDropdownOpen, setIdentityDropdownOpen] = useState(false);
    const [reviewsOpen, setReviewsOpen] = useState(() => location.pathname.startsWith('/reviews'));
    const identities = useIdentityStore(s => s.identities);
    const activeIdentityId = useIdentityStore(s => s.activeIdentityId);
    const setActiveIdentity = useIdentityStore(s => s.setActiveIdentity);
    const activeIdentity = useMemo(() => identities.find(i => i.id === activeIdentityId) || null, [identities, activeIdentityId]);
    const isModuleEnabled = useUserStore(s => s.isModuleEnabled);
    const displayName = useUserStore(s => s.displayName);
    const photoURL = useUserStore(s => s.photoURL);
    const userEmail = useUserStore(s => s.email);
    const membership = useUserStore(s => s.membership);

    useEffect(() => {
        if (location.pathname.startsWith('/reviews')) setReviewsOpen(true);
    }, [location.pathname]);

    const [organizerOpen, setOrganizerOpen] = useState(false);

    // The mobile drawer always shows the expanded layout, whatever the desktop
    // rail preference is. `railed` is the one flag the markup below reads.
    const railed = mobileOpen ? false : collapsed;
    const layout = railed ? 'rail' : 'row';

    // The customise control only exists in the expanded header, so the panel it
    // owns must not outlive it — otherwise its focus-restore target is gone.
    useEffect(() => {
        if (railed) setOrganizerOpen(false);
    }, [railed]);

    // ─── Auto-Zen: hide the sidebar entirely after inactivity during a session ───
    // (Collapsing during a session is AppShell's job; this is only the fade-out.)
    const isTimerRunning = useTimerStore(s => s.isRunning);
    const flowExecutionState = useFlowStore(s => s.executionState);
    const isSessionActive = isTimerRunning || flowExecutionState === 'running';
    const [zenHidden, setZenHidden] = useState(false);
    const inactivityTimerRef = useRef(null);

    useEffect(() => {
        if (!isSessionActive) {
            setZenHidden(false);
            return undefined;
        }

        const resetInactivityTimer = () => {
            setZenHidden(false);
            if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
            inactivityTimerRef.current = setTimeout(() => {
                setZenHidden(true);
            }, 5000);
        };

        resetInactivityTimer();

        document.addEventListener('mousemove', resetInactivityTimer);
        document.addEventListener('keydown', resetInactivityTimer);

        return () => {
            document.removeEventListener('mousemove', resetInactivityTimer);
            document.removeEventListener('keydown', resetInactivityTimer);
            if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
        };
    }, [isSessionActive]);

    const sidebarHidden = isSessionActive && zenHidden;

    // ─── Keyboard shortcuts ───
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Ignore when typing in inputs
            const tag = e.target.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;

            // [ key toggles sidebar collapse
            if (e.key === '[' && !e.metaKey && !e.ctrlKey) {
                onToggleCollapse();
            }
            // Escape closes identity dropdown
            if (e.key === 'Escape' && identityDropdownOpen) {
                setIdentityDropdownOpen(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onToggleCollapse, identityDropdownOpen]);

    // ─── Auto-close mobile drawer on route change ───
    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname, setMobileOpen]);

    const handleIdentitySwitch = (id) => {
        setActiveIdentity(id);
        setIdentityDropdownOpen(false);
    };

    // CommandPalette owns search and listens for ⌘K on `window`. Re-dispatching
    // that key is exactly how Header's search trigger does it — one opener, one
    // shortcut, no second code path to keep in sync.
    const handleSearchClick = () => {
        window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true }));
    };

    /**
     * Rail items need a tooltip, but `.sidebar__nav` scrolls and `.sidebar`
     * clips, so NavItem's absolutely-positioned tooltip can never paint here —
     * it gets suppressed (`tooltip={false}`) and the portal primitive is used
     * instead. Expanded rows show their label, so they need nothing.
     */
    const withRailTooltip = (key, label, node) => (
        railed
            ? <Tooltip key={key} label={label}>{node}</Tooltip>
            : <Fragment key={key}>{node}</Fragment>
    );

    // `Link`, not `NavLink`, throughout the nav. NavLink derives its OWN
    // `aria-current` and appends its own `active` class from a pathname match
    // that ignores the query string, so /reviews, /reviews?tab=weekly and
    // /reviews?tab=quarterly would ALL be announced as current and the stray
    // `active` class would land on unselected rows. The two predicates below
    // are the single source of truth; NavItem turns `selected` into both the
    // visual state and aria-current. (MobileBottomNav made the same call.)

    // Top-level items ignore the query string. Two items own more than their own
    // path: /projects owns its detail routes, and /focus owns every mode route —
    // Circuits and Rituals are tabs inside it, not rows of their own.
    const isItemActive = (path) =>
        location.pathname === path
        || (path === '/projects' && location.pathname.startsWith('/projects'))
        || (path === '/focus' && FOCUS_PATHS.includes(location.pathname));

    // Sub-nav children are distinguished BY their query string, so they need the
    // strict comparison — `/reviews` and `/reviews?tab=weekly` are different rows.
    const isChildPathActive = (path) => {
        if (path.includes('?')) {
            const [pathname, query] = path.split('?');
            return location.pathname === pathname && location.search.includes(query);
        }
        return location.pathname === path && (!location.search || location.search === '');
    };

    const renderNavItem = (item, index) => {
        const Icon = ICON_MAP[item.icon];

        // ── Collapsible group (Reviews) ──
        if (item.type === 'group') {
            const isChildActive = item.children.some(c => isChildPathActive(c.path));

            // In the rail there is nowhere to put the sub-nav, so the parent
            // becomes a plain link to the group's hub instead of a dead toggle.
            if (railed) {
                return withRailTooltip(`group-${item.label}`, item.label, (
                    <NavItem
                        as={Link}
                        to={item.path}
                        layout="rail"
                        icon={Icon}
                        label={item.label}
                        selected={isChildActive}
                        tooltip={false}
                        style={{ '--i': index }}
                    />
                ));
            }

            return (
                <div key={`group-${item.label}`} className="sidebar__group" style={{ '--i': index }}>
                    <NavItem
                        layout="row"
                        icon={Icon}
                        label={item.label}
                        selected={isChildActive}
                        onClick={() => setReviewsOpen(!reviewsOpen)}
                        aria-expanded={reviewsOpen}
                        aria-current={undefined}
                    >
                        <ChevronDown
                            className={`sidebar__chevron ${reviewsOpen ? 'sidebar__chevron--open' : ''}`}
                            aria-hidden="true"
                        />
                    </NavItem>
                    {reviewsOpen && (
                        <div className="sidebar__sub-nav animate-fade-in">
                            {item.children.map(child => (
                                <NavItem
                                    key={child.path}
                                    as={Link}
                                    to={child.path}
                                    layout="row"
                                    label={child.label}
                                    selected={isChildPathActive(child.path)}
                                    className="sidebar__item--child"
                                />
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        // ── Simple nav item ──
        return withRailTooltip(item.path, item.label, (
            <NavItem
                as={Link}
                to={item.path}
                layout={layout}
                icon={Icon}
                label={item.label}
                selected={isItemActive(item.path)}
                tooltip={false}
                style={{ '--i': index }}
            />
        ));
    };

    // ─── What the nav actually renders ───────────────────────────────────────
    // 1. module gating (unchanged), 2. dev-only gating (Design System — see
    // NAV_ITEMS above), 3. grouping (only when `navGroups` is on).
    // `import.meta.env.DEV` is read here, not baked into NAV_ITEMS at module
    // scope, so it is evaluated fresh on every render — the test suite relies
    // on that to flip it per-test with `vi.stubEnv`.
    const visibleItems = useMemo(
        () => NAV_ITEMS.filter(item =>
            (!item.module || isModuleEnabled(item.module))
            && (!item.devOnly || import.meta.env.DEV)
        ),
        [isModuleEnabled]
    );

    const navGroupsOn = isModuleEnabled('navGroups');
    const groups = useNavLayoutStore(s => s.groups);

    // With the module off we do NOT call the resolver — the flat, header-less
    // single section is the product default, not a degenerate case of a feature.
    const sections = useMemo(() => {
        if (!navGroupsOn) {
            return visibleItems.length ? [{ id: UNGROUPED_ID, label: null, items: visibleItems }] : [];
        }
        return resolveNavSections(groups, visibleItems);
    }, [navGroupsOn, groups, visibleItems]);

    // `--i` feeds `animation-delay: calc(min(--i, 8) * 40ms)`, so it has to be a
    // DENSE running counter across the FINAL render order — grouping reorders the
    // items, so it cannot be computed per section or from the source list. The
    // CSS clamp is the hard safety net; this makes the ripple actually sequential.
    const staggered = useMemo(() => {
        let order = 0;
        return sections.map(section => ({
            ...section,
            entries: section.items.map(item => ({ item, order: order++ })),
        }));
    }, [sections]);

    const planLabel = PLAN_LABELS[membership?.tier] || 'Free plan';
    const profileName = displayName || userEmail || 'Your profile';
    const avatarLetter = (displayName || userEmail || '?')[0].toUpperCase();

    // The identity block owns real chrome (padding + the separator above the
    // profile), so it must not render an empty shell when there is nothing to
    // show — in the rail with no active identity there is no control at all.
    const showIdentity = identities.length > 0 && (activeIdentity ? true : !railed);

    const profileButton = (
        <button
            type="button"
            className="btn btn-ghost btn-sm sidebar__profile"
            onClick={() => navigate('/settings')}
            aria-label={`${profileName} — ${planLabel}. Open settings`}
        >
            <span className="sidebar__profile-avatar" aria-hidden="true">
                {photoURL
                    ? <img src={photoURL} alt="" referrerPolicy="no-referrer" />
                    : <span>{avatarLetter}</span>}
            </span>
            {!railed && (
                <span className="sidebar__profile-text">
                    <span className="sidebar__profile-name">{profileName}</span>
                    <span className="sidebar__profile-plan">{planLabel}</span>
                </span>
            )}
        </button>
    );

    return (
        <>
        {/* Mobile hamburger — visible only on small screens */}
        <button
            className="sidebar__mobile-toggle"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
        >
            <Menu />
        </button>

        {/* Mobile backdrop */}
        {mobileOpen && (
            <div
                className="sidebar__backdrop"
                onClick={() => setMobileOpen(false)}
            />
        )}

        {/* `inert` when zen-hidden, for the same reason MobileBottomNav carries it:
            --zen-hidden only slides the sidebar off and fades it out, and
            `pointer-events: none` stops the mouse but does NOT remove a node from
            the tab order or the accessibility tree. Without this, every nav row,
            the profile and the rail toggle stayed keyboard-reachable while parked
            252px off-screen. Dropping the attribute restores them — no handler to
            keep in sync. `|| undefined` so the disabled case is an ABSENT
            attribute; `inert="false"` is still inert.
            The closed mobile drawer is handled in CSS instead (Sidebar.css), since
            that state is media-query-scoped and has no JS equivalent here. */}
        <aside className={`sidebar ${railed ? 'sidebar--collapsed' : ''} ${mobileOpen ? 'sidebar--mobile-open' : ''} ${sidebarHidden ? 'sidebar--zen-hidden' : ''}`} role="navigation" aria-label="Main navigation" inert={sidebarHidden || undefined}>

            <div className="sidebar__menu">
                {/* Brand header — lockup + actions expanded, the morphing mark in the rail */}
                <div className="sidebar__brand">
                    {railed ? (
                        <RailToggle onToggle={onToggleCollapse} />
                    ) : (
                        <>
                            <Logo variant="lockup" size="lg" className="sidebar__logo" />
                            <div className="sidebar__brand-actions">
                                <Tooltip label="Search  ⌘K" placement="bottom">
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-sm btn-icon sidebar__icon-btn"
                                        onClick={handleSearchClick}
                                        aria-label="Search"
                                    >
                                        <Search aria-hidden="true" />
                                    </button>
                                </Tooltip>

                                {navGroupsOn && (
                                    <Tooltip label="Customise navigation" placement="bottom">
                                        <button
                                            type="button"
                                            className="btn btn-ghost btn-sm btn-icon sidebar__icon-btn"
                                            onClick={() => setOrganizerOpen(true)}
                                            aria-haspopup="dialog"
                                            aria-expanded={organizerOpen}
                                            aria-label="Customise navigation"
                                        >
                                            <ListTree aria-hidden="true" />
                                        </button>
                                    </Tooltip>
                                )}

                                {/* In the mobile drawer "collapse to the rail"
                                    is meaningless — there is no rail — so the
                                    same control dismisses the drawer. Without
                                    this it would silently flip the DESKTOP
                                    preference and appear to do nothing. */}
                                <Tooltip label="Close sidebar" placement="bottom">
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-sm btn-icon sidebar__icon-btn"
                                        onClick={() => (mobileOpen ? setMobileOpen(false) : onToggleCollapse())}
                                        aria-label="Close sidebar"
                                        aria-expanded={true}
                                    >
                                        <PanelLeftClose aria-hidden="true" />
                                    </button>
                                </Tooltip>
                            </div>
                        </>
                    )}
                </div>

                {/* Navigation */}
                <nav className="sidebar__nav">
                    {staggered.map(section => (
                        <div className="sidebar__nav-section" key={section.id}>
                            {/* label is null for the ungrouped bucket — never a header */}
                            {section.label && !railed && (
                                <div className="sidebar__section-header">{section.label}</div>
                            )}
                            <div className="sidebar__nav-items">
                                {section.entries.map(({ item, order }) => renderNavItem(item, order))}
                            </div>
                        </div>
                    ))}
                </nav>
            </div>

            {/* Sync state sits ABOVE the footer's border. It is invisible whenever
                there is nothing to say, and inside the footer that invisible row
                was dead space that made the profile's padding look lopsided. Up
                here it also collapses to zero height when idle (Sidebar.css), so
                it costs nothing until it has something to report. */}
            <div className="sidebar__sync">
                <SyncStatus collapsed={railed} />
            </div>

            {/* Footer — identity picker, then the profile row (its own top border) */}
            <div className="sidebar__footer">
                {/* Identity Switcher — bottom of the sidebar, directly above the
                    profile. The card is a WRAPPER; the switcher itself is the
                    inner button. It used to be a `<div onClick>` with the settings
                    button nested inside — no role, no tabIndex, no key handler, so
                    the dropdown was unreachable by keyboard, and the two controls
                    were illegally nested. They are siblings now; the card keeps
                    its hover tint and (via :has) its focus ring.
                    The dropdown opens UPWARD (Sidebar.css) — at this height a
                    `top: 100%` menu would land off the bottom of the viewport. */}
                {showIdentity && (
                    <div className="sidebar__identity-switcher">
                        {activeIdentity && !railed && (
                            <div
                                className="sidebar__identity sidebar__identity--clickable"
                                style={{ '--identity-color': resolveEntityColor(activeIdentity.color) }}
                            >
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-sm sidebar__identity-main"
                                    onClick={() => setIdentityDropdownOpen(!identityDropdownOpen)}
                                    aria-haspopup="true"
                                    aria-expanded={identityDropdownOpen}
                                >
                                    <span className="sidebar__identity-emoji"><IconRenderer icon={activeIdentity.icon || activeIdentity.emoji} size={24} fallback="🎯" /></span>
                                    <span className="sidebar__identity-info">
                                        <span className="sidebar__identity-name">{activeIdentity.name}</span>
                                        <span className="sidebar__identity-label">Active Identity</span>
                                    </span>
                                </button>
                                <div className="sidebar__identity-actions">
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-sm btn-icon sidebar__identity-settings-btn"
                                        onClick={() => navigate('/identities')}
                                        aria-label="Manage identities"
                                    >
                                        <Settings />
                                    </button>
                                    <ChevronDown className={`sidebar__identity-chevron ${identityDropdownOpen ? 'sidebar__identity-chevron--open' : ''}`} aria-hidden="true" />
                                </div>
                            </div>
                        )}
                        {activeIdentity && railed && (
                            <Tooltip label="Switch identity">
                                <button
                                    type="button"
                                    className="sidebar__identity btn btn-ghost btn-sm btn-icon sidebar__identity--collapsed sidebar__identity--clickable"
                                    style={{ '--identity-color': resolveEntityColor(activeIdentity.color) }}
                                    onClick={() => setIdentityDropdownOpen(!identityDropdownOpen)}
                                    aria-label="Switch identity"
                                    aria-haspopup="true"
                                    aria-expanded={identityDropdownOpen}
                                >
                                    <span className="sidebar__identity-emoji"><IconRenderer icon={activeIdentity.icon || activeIdentity.emoji} size={20} fallback="🎯" /></span>
                                </button>
                            </Tooltip>
                        )}
                        {!activeIdentity && !railed && (
                            <button
                                type="button"
                                className="sidebar__identity sidebar__identity--clickable btn btn-tertiary sidebar__identity--empty"
                                onClick={() => setIdentityDropdownOpen(!identityDropdownOpen)}
                                aria-haspopup="true"
                                aria-expanded={identityDropdownOpen}
                            >
                                <span className="sidebar__identity-label">Select Identity</span>
                                <ChevronDown className="sidebar__identity-chevron" aria-hidden="true" />
                            </button>
                        )}

                        {/* Dropdown */}
                        {identityDropdownOpen && (
                            <div className="sidebar__identity-dropdown animate-fade-in">
                                {identities.map(i => (
                                    <button
                                        key={i.id}
                                        className={`sidebar__identity-option ${i.id === activeIdentityId ? 'sidebar__identity-option--active' : ''}`}
                                        style={{ '--opt-color': resolveEntityColor(i.color) }}
                                        onClick={() => handleIdentitySwitch(i.id)}
                                    >
                                        <span className="sidebar__identity-emoji"><IconRenderer icon={i.icon || i.emoji} size={20} fallback="🎯" /></span>
                                        {!railed && <span>{i.name}</span>}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Profile row — the avatar button and the settings icon are
                    SIBLINGS inside a wrapper (the identity card's pattern), never
                    nested: a <button> inside a <button> is invalid and the inner
                    one is unreachable. */}
                <div className="sidebar__profile-row">
                    {railed
                        ? <Tooltip label={`${profileName} — ${planLabel}`}>{profileButton}</Tooltip>
                        : profileButton}
                    {!railed && (
                        <Tooltip label="Settings" placement="top">
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm btn-icon sidebar__icon-btn"
                                onClick={() => navigate('/settings')}
                                aria-label="Settings"
                            >
                                <Settings aria-hidden="true" />
                            </button>
                        </Tooltip>
                    )}
                </div>
            </div>
        </aside>

        {/* Portalled to document.body — it must not live inside the sidebar,
            which transforms itself when zen-hiding. */}
        <NavOrganizer
            open={organizerOpen}
            onClose={() => setOrganizerOpen(false)}
            items={visibleItems}
        />
        </>
    );
}
